import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import React, { useEffect, useState } from 'react';
import { ChevronRight } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useProjectOptional } from '../../context/ProjectContext';

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) =>
    prop !== 'open' &&
    prop !== 'isSmallScreen' &&
    prop !== 'drawerWidth' &&
    prop !== 'type',
})(({ theme, open, type, isSmallScreen, drawerWidth }) => ({
  '& .MuiDrawer-paper': {
    position: 'fixed',
    top: 64,
    left: 0,
    height: 'calc(100vh - 64px)',
    whiteSpace: 'nowrap',
    width: (() => {
      if (type === 'documentation') return 250;
      if (isSmallScreen) return open ? drawerWidth : 0;
      return open ? drawerWidth : theme.spacing(7);
    })(),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
    boxSizing: 'border-box',
    overflowX: 'hidden',
    overflowY: open ? 'auto' : 'hidden',
    ...(!open && {
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(7),
      },
    }),
  },
}));

export default function Sidebar({
  MainListItems,
  open,
  toggleDrawer,
  type,
  isSmallScreen,
  drawerWidth,
  onSidebarNavigate,
}) {
  const projectId = useParams().projectId;
  const projectContext = useProjectOptional();
  const [lastProjectName, setLastProjectName] = useState('');

  const currentProject =
    projectContext?.projectId === projectId
      ? projectContext?.currentProject
      : null;

  useEffect(() => {
    if (currentProject?.name) {
      setLastProjectName(currentProject.name);
    }
  }, [currentProject?.name]);

  const projectName = currentProject?.name || lastProjectName;
  const hasProjectScope = !!projectId && !!projectName;

  return (
    <Drawer
      variant="permanent"
      open={open}
      type={type}
      drawerWidth={drawerWidth}
      isSmallScreen={isSmallScreen}
      sx={{
        mt: '64px',
        backgroundColor: '#fff',
      }}>
      {(hasProjectScope || type === 'dashboard') && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open && hasProjectScope ? 'space-between' : 'center',
            my: 2,
            p: 1,
            width: '100%',
          }}>
          {open && hasProjectScope && (
            <Typography
              variant="subtitle1"
              color="text.primary"
              sx={{
                pl: 1,
                whiteSpace: 'break-spaces',
                lineHeight: '1.5rem',
                fontWeight: 600,
                textAlign: 'left',
              }}>
              {projectName}
            </Typography>
          )}

          {type === 'dashboard' &&
            (open ? (
              <ChevronLeftIcon
                sx={{
                  fontSize: 22,
                  backgroundColor: '#1b3f87',
                  color: '#ffffff',
                  borderRadius: '50%',
                  cursor: 'pointer',
                }}
                onClick={toggleDrawer}
              />
            ) : (
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                <ChevronRight
                  onClick={toggleDrawer}
                  sx={{
                    fontSize: 22,
                    backgroundColor: '#1b3f87',
                    color: '#ffffff',
                    borderRadius: '50%',
                    cursor: 'pointer',
                  }}
                />
              </Box>
            ))}
        </Box>
      )}

      <Divider />

      <List component="nav" sx={{ padding: 0 }}>
        <MainListItems
          projectId={projectId}
          open={open}
          type={type}
          showIcons={type !== 'documentation' && !(isSmallScreen && open)}
          onSidebarNavigate={onSidebarNavigate}
        />
        <Divider sx={{ my: 1 }} />
      </List>
    </Drawer>
  );
}
